import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useMotionTier } from "@/lib/motionTier";
import { pinnedHeight, useWide } from "@/lib/storyMode";
import SectionHeading from "@/components/kit/SectionHeading";

const StoryScene = lazy(() => import("@/components/home/StoryScene"));

/**
 * Holds the place of the scroll story (the same height it will have, so nothing jumps) and loads it only when it is 600 px from the screen.
 * The wrapper carries data-section="story" for the home page's section tracking.
 * Laptops get the pinned story (one phone, steps change as you scroll); everything else gets four cards in normal scroll (lib/storyMode).
 */
export const STORY_STEPS = 4;

export default function StoryGate() {
  const tier = useMotionTier();
  const wide = useWide();
  const pinned = tier !== "static" && wide;
  const ref = useRef(null);
  const [mount, setMount] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver !== "function") { setMount(true); return undefined; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setMount(true); io.disconnect(); } }, { rootMargin: "600px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  // pinned: exactly the height the story will have. Cards: about 47 rem each (a phone and its words) until the story is there, then its own height
  const minHeight = pinned ? pinnedHeight(STORY_STEPS) : mount ? undefined : `${STORY_STEPS * 47}rem`;
  return (
    <section data-section="story" className="container mx-auto px-4 max-w-6xl 2xl:max-w-[80rem] py-8 md:py-12" aria-labelledby="story-heading">
      <SectionHeading id="story-heading" eyebrow="How it works" title="A coach in your pocket" line="Prop your phone up and play. It watches, talks and reports." />
      <div ref={ref} className="mt-6 lg:mt-10" style={{ minHeight }}>
        {mount ? <Suspense fallback={null}><StoryScene pinned={pinned} /></Suspense> : null}
      </div>
    </section>
  );
}
